import { X_AXIS_KEY } from "components/Graph/constants";
import { AggregatedTableData, NestedPaths } from "components/Graph/types";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils";

export class EntityGraphUtils<TData extends object> {
    private readonly data: TData[];
    private readonly metric: keyof TData;

    constructor(data: TData[], metric: keyof TData) {
        this.data = data;
        this.metric = metric;
    }

    public getGraphData<TPath extends keyof TData>(
        entity: TPath,
        entityIdPath: NestedPaths<TData>,
        filterBy: string[]
    ) {
        const entityEntries = new Map<string, TData>();
        const entriesByEntityAndDate = new Map<string, Map<string, TData>>();
        const datesSet = new Set<string>();

        for (const entry of this.data) {
            const entityId = GeneralGraphUtils.getValueFromPath(entry, entityIdPath);
            const date = GeneralGraphUtils.hasDateAt(entry)
                ? entry.date_at
                : GeneralGraphUtils.hasDatetimeAt(entry)
                  ? entry.datetime_at
                  : undefined;

            if (!GeneralGraphUtils.isString(entityId) || !date) {
                continue;
            }

            entityEntries.set(entityId, entityEntries.get(entityId) ?? entry);
            datesSet.add(date);

            const entriesByDate = entriesByEntityAndDate.get(entityId) ?? new Map<string, TData>();
            entriesByDate.set(date, entriesByDate.get(date) ?? entry);
            entriesByEntityAndDate.set(entityId, entriesByDate);
        }

        const uniqueEntities = Array.from(entityEntries.keys());
        const dates = Array.from(datesSet).sort();

        {
            /* two entities of same type with different ids can actually have the same name... */
        }
        const labelLookup = new Map<string, string>();

        const rawData = dates.map((date) => {
            {
                /* re-map date_at as datetime_at to keep it consistent... */
            }
            const row: { datetime_at: string; [key: string]: number | string } = { datetime_at: date };

            uniqueEntities.forEach((uniqueEntityValue) => {
                const entityMatchById = entityEntries.get(uniqueEntityValue);

                if (!entityMatchById) {
                    return;
                }

                const match = entriesByEntityAndDate.get(uniqueEntityValue)?.get(date);
                row[uniqueEntityValue] = match ? GeneralGraphUtils.resolveMetricValueToNumber(match[this.metric]) : 0;

                const resolvedLabel = GeneralGraphUtils.resolveEntityValue(entityMatchById[entity]);

                labelLookup.set(
                    uniqueEntityValue,
                    GeneralGraphUtils.isString(resolvedLabel) ? resolvedLabel : uniqueEntityValue
                );
            });

            return row;
        });

        return {
            graphData: GeneralGraphUtils.filterDataByEntities(rawData, filterBy),
            uniqueEntities: uniqueEntities.filter((entity) => filterBy.length === 0 || filterBy.includes(entity)),
            labelLookup,
        };
    }

    public getHighlightsData(entityKey: keyof TData, selectedEntities: string[]): AggregatedTableData<TData>[] {
        const result: Record<string | number, AggregatedTableData<TData>> = {};

        this.data.forEach((row) => {
            const rawEntity = row[entityKey];
            const entity = GeneralGraphUtils.resolveEntityValue(rawEntity);

            const entityId = GeneralGraphUtils.resolveEntityId(rawEntity);

            if (!entity || (selectedEntities.length > 0 && !selectedEntities.includes(entityId))) {
                return;
            }

            if (!result[entity]) {
                result[entity] = { entity } as AggregatedTableData<TData>;
            }

            (Object.keys(row) as (keyof TData)[]).forEach((key) => {
                if (key === entityKey || key === X_AXIS_KEY || key === "date_at") {
                    return;
                }

                const value = row[key];

                if (GeneralGraphUtils.isMoneyData(value)) {
                    const moneyValue = GeneralGraphUtils.resolveMetricValueToOrigin(value);

                    if (moneyValue !== null && typeof moneyValue !== "number" && typeof moneyValue !== "string") {
                        const prevRecord = result[entity][key];

                        if (prevRecord && GeneralGraphUtils.isMoneyData(prevRecord)) {
                            prevRecord.amount += moneyValue.amount;
                        } else {
                            // sketchy assertion but the code leading up to it is imo defensive enough
                            result[entity][key] = { ...moneyValue } as never;
                        }

                        return;
                    }
                }

                if (GeneralGraphUtils.isNumber(value)) {
                    const prevRecord = result[entity][key];

                    if (prevRecord && GeneralGraphUtils.isNumber(prevRecord)) {
                        result[entity][key] = (prevRecord + value) as never;
                    } else {
                        result[entity][key] = value as never;
                    }
                }
            });

            result[entity]["entityId" as keyof TData] = entityId as never;
        });

        return Object.values(result);
    }
}
